import { CompendiumItem, ContentPack, ItemType } from '@/class'
import { CompendiumStore } from '@/stores'
import { ITagData } from '@/interface'
import logger from '@/user/logger'
import { applyLcpTracking, type ILcpTracked } from './LcpItemMixin'
import { ByTier, replaceVal, resolveTier } from '@/util/tierFormat'

export interface ITagCompendiumData {
  id: string
  name: string
  description: string
  filter_ignore?: boolean
  hidden?: boolean
}

class Tag implements ILcpTracked {
  public readonly ID: string
  public readonly FilterIgnore: boolean
  public readonly IsHidden: boolean
  public readonly ItemType: ItemType
  public readonly IsUnique: boolean
  public readonly IsAI: boolean
  public readonly NoCascade: boolean
  public readonly IsExotic: boolean
  public readonly IsDangerZone: boolean
  public readonly IsLimited: boolean
  public readonly IsLoading: boolean
  public readonly IsRecharging: boolean
  public readonly IsIndestructible: boolean
  public readonly IsSmart: boolean
  public readonly IsHeatCost: boolean
  public readonly IsOverkill: boolean
  public LcpName: string = ''
  public InLcp: boolean = false
  public readonly UsageCost: number = 0
  private _name: string
  private _val: number | string
  private _description: string

  public constructor(tagData: ITagCompendiumData, pack?: ContentPack) {
    this.ID = tagData.id
    this._name = tagData.name
    this._description = tagData.description
    this._val = ''
    this.IsHidden = tagData.hidden || false
    this.FilterIgnore = tagData.filter_ignore || this.IsHidden
    this.ItemType = ItemType.Tag
    this.IsUnique = this.ID === 'tg_unique'
    this.IsAI = this.ID === 'tg_ai'
    this.NoCascade = this.ID === 'tg_no_cascade'
    this.IsLimited = this.ID === 'tg_limited'
    this.IsLoading = this.ID === 'tg_loading'
    this.IsRecharging = this.ID === 'tg_recharge'
    this.IsIndestructible = this.ID === 'tg_indestructible'
    this.IsSmart = this.ID === 'tg_smart'
    this.IsHeatCost = this.ID === 'tg_heat_self'
    this.IsOverkill = this.ID === 'tg_overkill'
    this.IsExotic = this.ID === 'tg_exotic'
    this.IsDangerZone = this.ID === 'tg_danger_zone'

    if (this.ID === 'tg_full') this.UsageCost = 2
    else if (this.ID === 'tg_quick') this.UsageCost = 1

    applyLcpTracking(this, pack)

    if (!this._val && this._name.includes('{VAL}')) {
      this._val = 1
    }
  }

  public get Name(): string {
    return ByTier(replaceVal(this._name, 'X'))
  }

  public get Value(): number | string {
    return this._val
  }

  public set Value(val: number | string) {
    this._val = val
  }

  public get Description(): string {
    return ByTier(replaceVal(this._description, 'X'))
  }

  public GetDescription(addBonus?: number, tier?: number): string {
    let out = this._description
    let bonus = 0
    if (this.ID === 'tg_limited') bonus = addBonus || 0
    if (this._val) {
      if (typeof this._val === 'number') {
        let r = this._val.toString()
        if (bonus)
          r = `${(this._val + bonus).toString()} <span class="caption text--secondary">(Limited ${
            this._val
          } + ${bonus} bonus)</span>`
        out = this._description.replace(/{VAL}/g, r)
      } else {
        const str = String(this._val)
        if (str.includes('+')) {
          const split = str.split('+')
          const newVal = `${split[0]}+${parseInt(split[1]) + bonus}`
          const newDesc = this._description.replace(/{VAL}/g, newVal)
          out = bonus ? `${newDesc} (+${bonus})` : newDesc
        } else {
          out =
            bonus > 0
              ? this._description.replace(/{VAL}/g, `${this._val}+${bonus}`)
              : this._description.replace(/{VAL}/g, this._val)
        }
      }
    }

    if (tier) out = resolveTier(out, tier)

    return out
  }

  public GetName(addBonus?: number, tier?: number): string {
    let out = this._name
    let bonus = 0
    if (this.IsLimited) bonus = addBonus || 0
    if (this._val) {
      if (typeof this._val === 'number') {
        out = this._name.replace(/{VAL}/g, (this._val + bonus).toString())
      } else {
        const str = String(this._val)
        if (str.includes('+')) {
          const split = str.split('+')
          const newVal = `${split[0]}+${parseInt(split[1]) + bonus}`
          const newName = this._name.replace(/{VAL}/g, newVal)
          out = bonus ? `${newName} (+${bonus})` : newName
        } else {
          out =
            bonus > 0
              ? this._name.replace(/{VAL}/g, `${this._val}+${bonus}`)
              : this._name.replace(/{VAL}/g, this._val)
        }
      }
    }

    if (tier) out = resolveTier(out, tier)

    return out
  }

  public static _genTag(id: string): Tag {
    const t = CompendiumStore().TagData.find(x => x.id === id || (x as any).ID === id)
    if (!t) throw new Error(`Tag ${id} not found`)
    if ((t as any).ID !== undefined) return t as unknown as Tag
    return new Tag(t)
  }

  public static Populate(item: CompendiumItem): Tag[] {
    const tags = [] as Tag[]
    if (item.Deployables) {
      if (item.Deployables.some(x => x.Type === 'Drone')) tags.push(Tag._genTag('tg_drone'))
      if (item.Deployables.some(x => x.Type === 'Mine')) tags.push(Tag._genTag('tg_mine'))
      if (item.Deployables.some(x => x.Type !== 'Drone' && x.Type !== 'Mine'))
        tags.push(Tag._genTag('tg_deployable'))
    }
    // if (item.Actions) {
    //   if (item.Actions.some(x => x.Activation === ActivationType.Protocol))
    //     tags.push(Tag._genTag('tg_protocol'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.Quick))
    //     tags.push(Tag._genTag('tg_quick_action'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.QuickTech))
    //     tags.push(Tag._genTag('tg_quick_tech'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.Full))
    //     tags.push(Tag._genTag('tg_full_action'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.FullTech))
    //     tags.push(Tag._genTag('tg_full_tech'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.Reaction))
    //     tags.push(Tag._genTag('tg_reaction'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.Free))
    //     tags.push(Tag._genTag('tg_free_action'))
    //   if (item.Actions.some(x => x.Activation === ActivationType.Invade))
    //     tags.push(Tag._genTag('tg_invade'))
    // }
    return tags
  }

  public static Serialize(tags: Tag[]): ITagData[] {
    return tags.map(x => {
      return {
        id: x.ID,
        val: x.Value,
      }
    })
  }

  public static Deserialize(
    data: ITagData[],
    packTags?: ITagCompendiumData[],
    packName?: string
  ): Tag[] {
    const output = [] as Tag[]
    if (!data) return output
    data.forEach(x => {
      let t: Tag
      if (CompendiumStore().TagData.find(t => t.id === x.id || (t as any).ID === x.id)) t = Tag._genTag(x.id)
      else {
        if (!packTags) throw new Error(`LCP data not provided for tag id: ${x.id}`)
        const pt = packTags.find(t => t.id === x.id)
        if (!pt) {
          logger.error(
            `Tag ${x.id} not found in pack ${packName} (or has uninstalled dependencies)`,
            this
          )
          return
        }
        t = new Tag(pt)
      }

      if (x.val) t.Value = x.val

      output.push(t)
    })
    return output
  }

  public get Icon(): string {
    return 'mdi-tag-outline'
  }

  public get IsCombatTag(): boolean {
    const nonCombatRelevant = [
      'tg_quick',
      'tg_full',
      'tg_free',
      'tg_full',
      'tg_tech_quick',
      'tg_tech_full',
      'tg_protocol',
      'tg_gear',
      'tg_mod',
      'tg_modded',
      'tg_exotic',
      'tg_limited',
      'tg_personal_armor',
      'tg_pilot_weapon',
      'tg_unique',
    ]
    return !nonCombatRelevant.includes(this.ID)
  }
}

export default Tag
